'use client';

type FirebaseErrorLike = string | ({ code?: string; message?: string } & Record<string, unknown>) | null | undefined


/**
 * Firebase Global Error Classifier
 * (Auth + Firestore 공용)
 *
 * - code → type → level → message 자동 매핑
 */

const CODE_TO_MESSAGE: Record<string, string> = {
  /* =============================
   * 🔹 Firebase Auth (User / 계정)
   * ============================= */
  'auth/invalid-credential': '이메일 또는 비밀번호가 올바르지 않습니다.',
  'auth/wrong-password': '이메일 또는 비밀번호가 올바르지 않습니다.',
  'auth/user-not-found': '가입되지 않은 계정입니다.',
  'auth/invalid-email': '올바르지 않은 이메일 주소입니다.',
  'auth/email-already-in-use': '이미 사용 중인 이메일입니다.',
  'auth/weak-password': '비밀번호는 최소 6자 이상 입력해주세요.',
  'auth/requires-recent-login': '보안을 위해 최근 로그인 후 다시 시도해주세요.',

  // 네트워크 / 일시 상태
  'auth/network-request-failed': '네트워크 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
  'auth/too-many-requests': '요청이 너무 많습니다. 잠시 후 다시 시도해주세요.',

  // 정책 / 환경
  'auth/operation-not-allowed': '현재 요청이 허용되지 않습니다. 관리자에게 문의해주세요.',
  'auth/user-disabled': '비활성화된 계정입니다. 관리자에게 문의해주세요.',

  // 사용자 행동
  'auth/popup-closed-by-user': '로그인이 취소되었습니다.',
  'auth/cancelled-popup-request': '이전 로그인 요청이 취소되었습니다. 다시 시도해주세요.',
  'auth/popup-blocked': '팝업이 차단되었습니다. 브라우저 팝업을 허용해주세요.',
  'auth/account-exists-with-different-credential': '이미 다른 방법으로 가입한 이메일입니다. 처음 가입한 방법으로 로그인해주세요.',
  'auth/missing-email': '이메일을 입력해주세요.',
  'auth/quota-exceeded': '요청이 너무 많습니다. 잠시 후 다시 시도해주세요.',
  'auth/user-token-expired': '로그인이 끊겼어요. 다시 로그인해 주세요.',
  'auth/internal-error': '요청 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',


  /* =============================
   * 🔹 Firestore (DB / 권한 / 상태)
   * ============================= */
  'firestore/permission-denied': '데이터에 접근할 권한이 없습니다.',
  'firestore/failed-precondition': '요청을 처리할 수 없는 상태입니다.',
  'firestore/unavailable': '현재 데이터 서비스를 사용할 수 없습니다. 잠시 후 다시 시도해주세요.',
  'firestore/deadline-exceeded': '요청 시간이 초과되었습니다.',
  'firestore/aborted': '요청이 중단되었습니다. 다시 시도해주세요.',
  'firestore/not-found': '요청한 데이터가 존재하지 않습니다.',
};

const SYSTEM_FATAL_CODES = [
  // Auth 정책 / 환경 / 설정
  'auth/operation-not-allowed',
  'auth/internal-error',
  'auth/user-disabled',

  // Firestore 정책 / 보안
  'firestore/permission-denied',
  'firestore/failed-precondition',
];

/* ===============================
 *  기본 유틸
 * =============================== */

const extractCode = (error: FirebaseErrorLike) => {
  if (!error) return '';
  const raw = typeof error === 'string' ? error : error.code || '';
  // Firestore 웹 SDK 는 접두 없이 `permission-denied` 로 준다 — 표의 `firestore/…` 로 맞춘다
  if (raw && typeof error !== 'string' && !raw.includes('/') && FIRESTORE_BARE_CODE.test(raw)) return `firestore/${raw}`;
  if (raw) return raw;

  const msg = typeof error === 'string' ? error : error.message || '';
  const match = msg.match(/(auth|firestore)\/[\w-]+/i);
  return match ? match[0] : '';
};

/** Firestore 상태 코드(접두 없는 꼴) */
const FIRESTORE_BARE_CODE = /^(cancelled|unknown|invalid-argument|deadline-exceeded|not-found|already-exists|permission-denied|resource-exhausted|failed-precondition|aborted|out-of-range|unimplemented|internal|unavailable|data-loss|unauthenticated)$/;

/**
 * Firebase 공통 메시지 매핑 — 표에 있는 코드만 문장을 준다.
 * ⚠️ 표에 없으면 `''` — SDK 원문(「Firebase: Error (auth/…)」)은 사용자 화면에 내지 않는다. 부르는 쪽이 제 문장을 쓴다.
 */
const getFirebaseErrorMessage = (error: FirebaseErrorLike) => {
  const code = extractCode(error);
  return code && CODE_TO_MESSAGE[code] ? CODE_TO_MESSAGE[code] : '';
};

/* 사용자 행동 유형 */
const isInfoLevelFirebaseError = (error: FirebaseErrorLike) => {
  const code = extractCode(error);
  return (
    code === 'auth/popup-closed-by-user' ||
    code === 'auth/cancelled-popup-request'
  );
};

/* ===============================
 *  🔹 Auth + Firestore 공통 분류기
 * =============================== */

function classifyFirebaseError(error: FirebaseErrorLike) {
  const code = extractCode(error);

  // 사용자 행동 (INFO)
  if (isInfoLevelFirebaseError(error)) {
    return { domain: 'auth', type: 'user-action', level: 'info' };
  }

  // Auth 사용자 입력 오류 (ERROR)
  if ([
    'auth/invalid-credential',
    'auth/wrong-password',
    'auth/user-not-found',
    'auth/email-already-in-use',
    'auth/weak-password',
    'auth/invalid-email',
    'auth/requires-recent-login',
  ].includes(code)) {
    return { domain: 'auth', type: 'user-input', level: 'error' };
  }

  // Auth 네트워크 / 재시도 가능 (ERROR)
  if ([
    'auth/network-request-failed',
    'auth/too-many-requests',
  ].includes(code)) {
    return { domain: 'auth', type: 'retryable', level: 'error' };
  }

  // Firestore 재시도 가능 (ERROR)
  if ([
    'firestore/unavailable',
    'firestore/deadline-exceeded',
    'firestore/aborted',
  ].includes(code)) {
    return { domain: 'firestore', type: 'retryable', level: 'error' };
  }

  // 정책 / 보안 / 환경 이상 (FATAL)
  if (SYSTEM_FATAL_CODES.includes(code)) {
    return { domain: code.startsWith('auth') ? 'auth' : 'firestore', type: 'policy-or-config', level: 'fatal' };
  }

  // not-found 이지만 정상 과정이 아니면 → 무결성 붕괴 (FATAL)
  if (/not-found/i.test(code)) {
    return { domain: 'firestore', type: 'unexpected-missing', level: 'fatal' };
  }

  // 정의되지 않은 코드는 예상치 못한 에러이므로 FATAL로 처리
  if (code && !CODE_TO_MESSAGE[code] && !SYSTEM_FATAL_CODES.includes(code)) {
    return { domain: code.startsWith('firestore') ? 'firestore' : 'auth', type: 'unknown', level: 'fatal' };
  }

  // 기본값 (code가 없는 경우도 FATAL로 처리)
  return { domain: code?.startsWith('firestore') ? 'firestore' : 'auth', type: 'unknown', level: 'fatal' };
}

/**
 * 이메일·비밀번호 로그인에서 「계정이 없거나 비밀번호가 틀림」인지.
 * ⚠️ 이메일 존재 여부 숨김이 켜져 있어 Firebase 는 두 경우를 가르지 않는다 — 없는 이메일도 `auth/invalid-credential` 로 온다.
 */
export function isLoginCredentialMismatch(error: unknown): boolean {
  return ['auth/invalid-credential', 'auth/wrong-password', 'auth/user-not-found'].includes(extractCode(error as FirebaseErrorLike));
}

/* ===============================
 *  🔹 최종 결과 Resolver
 * =============================== */

export function resolveFirebaseError(error: FirebaseErrorLike, options: Record<string, unknown> = {}) {
  const { side } = options;
  const code = extractCode(error);
  const { domain, type, level } = classifyFirebaseError(error);
  const message = getFirebaseErrorMessage(error);

  return {
    code,
    domain,   // 🔹 auth / firestore
    type,     // 🔹 user-input / retryable / policy
    level,    // 🔹 info / error / fatal
    message,  // 🔹 표에 있는 코드의 문장 · 없으면 ''
    ...(side ? { side } : {}), // 🔹 client / server (옵션) — `'' 스프레드`와 동일(무변)
  };
}
